// ═══════════════════════════════════════════════════════════════════════════════
// src/lib/supabaseClient.js
// Singleton Supabase client for PracticeOS Lite.
// ═══════════════════════════════════════════════════════════════════════════════
//
// Environment variables (set in .env.local and Vercel):
//   VITE_SUPABASE_URL       = https://wlkwmfxmrnjqvcsbwksk.supabase.co
//   VITE_SUPABASE_ANON_KEY  = <your anon public key>
//
// Never expose the service_role key in client code — it bypasses RLS.
// ═══════════════════════════════════════════════════════════════════════════════

import { createClient } from "@supabase/supabase-js";
import { transientRetryFetch } from "./transientFetch.js";
import { auditRpcArgs } from "./auditArgs.js";

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // eslint-disable-next-line no-console
  console.error(
    "[PracticeOS] Missing Supabase env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local"
  );
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    storageKey: "practiceos.auth",
    // Disable navigator.locks-based cross-tab sync. It is the documented cause
    // of "Lock 'lock:...' was released because another request stole it" errors
    // during React StrictMode double-mounts and rapid auth events. PracticeOS
    // is a single-tab workflow; we don't need cross-tab session coordination.
    lock: async (_name, _acquireTimeout, fn) => await fn(),
  },
  global: {
    // Sent on every request, including supabase.functions.invoke. Any edge
    // function called with invoke must list x-application-name in its
    // Access-Control-Allow-Headers (or echo the request's headers), or Chrome
    // blocks the call after the preflight.
    headers: { "x-application-name": "practiceos-lite" },
    // Retries PostgREST answers that mean "this never ran" (statement timeout
    // 57014, schema-cache/pool 503s) - the cold-database first load after
    // idle. See transientFetch.js; removing this fails the contract test.
    fetch: transientRetryFetch,
  },
});

// Convenience helpers -----------------------------------------------------------

export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user;
}

export function onAuthChange(cb) {
  const { data } = supabase.auth.onAuthStateChange((event, session) => cb(event, session));
  return () => data.subscription.unsubscribe();
}

export async function signInWithEmail(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

// Checks a password for the signed-in person WITHOUT touching the app's
// session: a throwaway client (no storage, no refresh) signs in, and its own
// session is ended at once. Used by the portal before a password change.
export async function verifyPasswordOnly(email, password) {
  const probe = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: "pos-password-check" },
  });
  const { data, error } = await probe.auth.signInWithPassword({ email, password });
  if (error || !data?.session) return { ok: false, error };
  // supabase-best-effort: ending the throwaway session; the check already passed
  await probe.auth.signOut({ scope: "local" }).catch(() => {});
  return { ok: true, userId: data.user?.id || null };
}

// Ends THIS device's session only. supabase-js defaults to scope "global",
// which revokes every refresh token the user holds: until Oct 2026 an idle
// phone timing out (or a tap on Sign out there) silently signed the same
// person out of their clinic workstation too, which then failed its next
// token refresh (refresh_token_not_found) and sent its in-flight reads with
// only the anon key. "Sign out everywhere" is a separate, explicit action
// (portal Security). Guarded by src/lib/__tests__/signOutScope.contract.test.mjs.
export async function signOut() {
  const { error } = await supabase.auth.signOut({ scope: "local" });
  if (error) throw error;
}

// Audit a failed staff sign-in --------------------------------------------------
// A failed sign-in has no session, so the request carries only the anon key.
// log_audit is not granted to anon (it answered 401 and, until Oct 2026, no
// failed sign-in was ever recorded). log_failed_login is the one audit write a
// signed-out browser may make: it can only write a "Failed Login" row and is
// flood-guarded server-side (20270110100000_failed_login_audit.sql). Guarded
// by src/lib/__tests__/failedLoginAudit.contract.test.mjs.
export async function logFailedLogin({ email, error = null }) {
  const { error: rpcError } = await supabase.rpc("log_failed_login", {
    p_email: email ? String(email) : null,
    p_error: error ? String(error) : null,
  });
  if (rpcError) {
    // Never block the sign-in screen because of an audit-log failure
    // eslint-disable-next-line no-console
    console.warn("[PracticeOS] failed-login audit failed:", rpcError);
    return false;
  }
  return true;
}

// Append a row to audit_log via the server-side function ------------------------
// Use this for any PHI read/write the app performs. Fires server-side so role
// + user_id + practice_id are pulled from the JWT, not the client.
export async function logAudit({ action, entityType, entityId, patientId = null, details = {}, success = true, error = null }) {
  // auditRpcArgs sends a non-UUID id as null (flagged in details) so a bad id
  // cannot make PostgREST reject the call and lose the row.
  const { data, error: rpcError } = await supabase.rpc("log_audit",
    auditRpcArgs({ action, entityType, entityId, patientId, details, success, error }));
  if (rpcError) {
    // Never break the user flow because of an audit-log failure
    // eslint-disable-next-line no-console
    console.warn("[PracticeOS] audit log failed:", rpcError);
    return null;
  }
  return data;
}
